import { businessDayOf } from '@app/shared/time';
import { OrderLineType } from '@app/shared/constants-server';
import type { DayState, RentalBillingParams, RentalClosure, RentalLineView, RentalViewStatus } from './rental-billing.model';
import { frDay } from './rental-days';
import type { InterruptedKind } from './rental-planning.geometry';
import { interruptedOn } from './rental-planning.geometry';

// Libellés de structure (statuts, états de jour, paramètres de carte) ; les textes métier restent en c_values.
export const STATUS_LABELS: Record<RentalViewStatus, string> = {
  return: 'Reprise',
  not_started: 'Non livré',
  blocked: 'Bloqué',
  up_to_date: 'À jour',
  due: 'À facturer',
  interrupted: 'Interrompu',
  finished: 'Durée atteinte',
  closed: 'Clos',
};

export const INTERRUPTED_BADGE = 'Interrompu';

// Explication de chaque statut du moteur (lifecycle.rs screen_status), en infobulle du libellé commun.
export const STATUS_HINTS: Record<RentalViewStatus, string> = {
  due: 'Des périodes sont échues et non facturées : « Facturer » (écran Locations) les émettra.',
  up_to_date: 'Toutes les périodes échues sont facturées.',
  finished: 'La Durée prévue est atteinte : le moteur ne facture plus cette ligne.',
  closed: 'Location close (reprise ou fin de facturation décidée) : tout ce qui était dû est facturé.',
  interrupted: 'Une interruption ouverte suspend la facturation de la ligne.',
  blocked: 'Ligne en anomalie : le moteur ne la facture pas.',
  not_started: 'Aucun bon de livraison livré ne porte la ligne : la facturation n\'a pas démarré.',
  return: 'Ligne de récupération : date du bon de livraison qui la porte.',
};

export const DAY_STATE_LABELS: Record<DayState, string> = {
  BILLED: 'Facturé',
  BILLED_EARLY: 'Facturé avant livraison',
  PAID_INTERRUPTED: 'Payé, interrompu',
  COMPENSATED: 'Payé par report',
  DUE: 'Échu, à facturer',
  RUNNING: 'En cours',
  UPCOMING: 'À venir',
  INTERRUPTED: 'Interrompu',
  WAITING_START: 'Facturation pas commencée',
  OFFERED: 'Offert',
  RETURNED: 'Repris ou fin de facturation',
  OUTSIDE: 'Hors location',
};

// Jours INTERRUPTED du Planning selon les pièces (interruptedKindAt) ; la légende les montre tous trois dès qu'une interruption est visible.
export const INTERRUPTED_KIND_LABELS: Record<InterruptedKind, string> = {
  free: 'Interrompu, non facturé',
  carried: 'Interrompu, facturé, reporté',
  credit_due: 'Interrompu, facturé, avoir à émettre',
  credited: 'Interrompu, facturé, avoir émis',
};

// Jours payés devenus non dus hors interruption (décès, fin, reprise…) : mêmes couleurs que les jours interrompus (creditKindAt).
export const CREDIT_KIND_LABELS: Record<'credit_due' | 'credited', string> = {
  credit_due: 'Facturé, avoir à émettre',
  credited: 'Facturé, avoir émis',
};

export const OUTSIDE_PRESENT_LABEL = 'Non facturé (Durée atteinte ou hors prescription)';

// Section Carte de la fiche de ligne seulement : aucun avertissement ni texte d'effet ne s'en sert.
export const INTERRUPTION_RULE_LABELS: Record<RentalBillingParams['interruption_rule'], string> = {
  PAUSE: 'Pause : la période attend la reprise',
  FULL_PERIOD: 'Période entamée due en entier',
  PRORATA: 'Prorata des jours',
};

export const END_RULE_LABELS: Record<RentalBillingParams['end_rule'], string> = {
  FULL_PERIOD: 'Dernière période due en entier',
  PRORATA: 'Dernière période au prorata',
};

export const OVERBILLING_LABELS: Record<RentalBillingParams['overbilling'], string> = {
  CREDIT_NOTE: 'Trop-facturé : avoir partiel',
  COMPENSATION: 'Trop-facturé : report sur les périodes suivantes',
};

export const PRICE_SOURCE_LABELS: Record<RentalBillingParams['price_source'], string> = {
  CARD: 'Prix du contrat de location',
  LPP_TARIF: 'Tarif LPP de responsabilité',
  LPP_PLV: 'Prix limite de vente LPP',
};

// Drapeaux de carte de la section Carte de la fiche : [vrai, faux].
export const CARD_FLAG_LABELS: Record<'return_day_billed' | 'start_before_delivery' | 'prescription_required', [string, string]> = {
  return_day_billed: ['Facturé', 'Non facturé'],
  start_before_delivery: ['Facturation permise', 'Pas de facturation'],
  prescription_required: ['Exigée', 'Non exigée'],
};

export const DOC_TYPE_LABELS: Record<string, string> = {
  FACTURE: 'Facture',
  FACTURE_FSE: 'Facture FSE',
  AVOIR: 'Avoir',
};

export function statusLabel(status: RentalViewStatus | string | null | undefined): string {
  return status ? (STATUS_LABELS[status as RentalViewStatus] ?? String(status)) : '';
}

export function statusHint(status: RentalViewStatus | string | null | undefined): string {
  return status ? (STATUS_HINTS[status as RentalViewStatus] ?? '') : '';
}

export function statusClass(status: RentalViewStatus | string | null | undefined): string {
  return 'st-' + (status ?? 'unknown');
}

export function dayStateLabel(state: DayState | null | undefined): string {
  return state ? (DAY_STATE_LABELS[state] ?? state) : '';
}

export function dayStateClass(state: DayState): string {
  return 's-' + state.toLowerCase();
}

export function docTypeLabel(docType: string | null | undefined): string {
  return docType ? (DOC_TYPE_LABELS[docType] ?? docType) : '';
}

// Patient d'une ligne : son mémo, sinon le bénéficiaire du dossier (JP 30/10 : dossiers HAD sans mémo).
export function patientLabel(memo: string | null | undefined, beneficiary: string | null | undefined): string {
  return memo?.trim() || beneficiary?.trim() || 'Patient non renseigné';
}

// Désignation d'une ligne louée : l'intitulé de son contrat de location, l'article qui l'a appelé en second (JP 04/11/2026).
export interface RentalNamed { terms_name?: string | null; product_name?: string | null; }
export function rentalTitle(l: RentalNamed): string | null {
  return l.terms_name || l.product_name || null;
}
// Article sous l'intitulé du contrat ; null sans contrat, le titre est alors déjà l'article.
export function rentalSubtitle(l: RentalNamed): string | null {
  return l.terms_name ? l.product_name || null : null;
}
// Ligne de commande telle que lue (Sales_order_l) : l'intitulé de sa copie du contrat, pour une ligne louée seulement ; une ancienne RENTAL_RETURN garde « Reprise … ».
export function orderLineNamed(l: { typ?: string | null; rental_terms_snapshot?: { name?: string | null } | null; product_name?: string | null }): RentalNamed {
  return { terms_name: l.typ === OrderLineType.RENTAL ? l.rental_terms_snapshot?.name : null, product_name: l.product_name };
}

// Pour qui est une pièce : ses mémos (patient, chambre) jusqu'à trois, au-delà leur nombre ; title = la liste entière.
export function docPatients(memos: string[] | null | undefined): { text: string; title: string } {
  const m = memos ?? [];
  return { text: m.length > 3 ? `${m.length} patients` : m.join(', '), title: m.join('\n') };
}

// Clôture 2107 en vigueur (non annulée) : la ligne est figée, aucune décision ni BL tant qu'elle n'est pas rouverte.
export const CLOSED_LINE = 'Ligne close : la rouvrir d\'abord';
export function lineClosed(l: { closure?: RentalClosure | null } | null | undefined): boolean {
  return !!l?.closure && !l.closure.cancelled;
}

// Infobulle de la pastille « close » : quand, et si le passage l'a posée d'office.
export function closureTip(c: RentalClosure): string {
  const day = businessDayOf(c.created.dat);
  return `Ligne close${day ? ' le ' + frDay(day) : ''}${c.auto ? ' d\'office par le passage' : ''} : plus de facture, d'avoir ni de décision tant qu'elle n'est pas rouverte (Rouvrir sur la page du dossier).`;
}

// Seconde pastille « Interrompu » : ligne interrompue au jour du calcul dont le statut dit autre chose (à facturer, à jour…).
export function showInterruptedBadge(l: RentalLineView, asOf: string): boolean {
  return l.status !== 'interrupted' && interruptedOn(l, asOf);
}
